import isEqual from 'lodash/isEqual';

import { doRequest } from '@r1-frontend/do-request';
import { addressInfo } from '@r1-frontend/api-domru/full-buy/v1/sale-agent/address-company';
import { ResponseDto } from '@r1-frontend/api-domru/full-buy/v1/sale-agent/address-company/dto/ResponseDto';
import { ESaleAgentClientName } from '@r1-frontend/api-domru/full-buy/v1/sale-agent/saleAgentTypes';
import { selectAddressByString } from '@r1-frontend/entities/ContactAddress/selectors/selectAddressByString';

import { IAddress } from '@r1-frontend/shared/types/IAddress';
import { ICitiesOption, ISelectInputOption } from '@r1-frontend/shared/types/ISelectInputOption';
import { TDispatch } from '@r1-frontend/shared/types/Redux';

import { EContactDetailTypes, TSetAddressVerifiationAction } from '../index';
import { EAvailableSpeed, EVerificationStatus, IContactAddressState, IVerifiedAddressData } from '../initialState';

export const setAddressVerification = ({
    address,
    gigabitAvail,
    isPrivate,
    isHouseConnected,
    companyId,

    availableSpeed,
    isHouseCottage,
}: Omit<IVerifiedAddressData, 'status'>): TSetAddressVerifiationAction => {
    return ({
        type: EContactDetailTypes.SET_VERIFIED_ADDRESS,
        payload: {
            verifiedAddressData: {
                address,
                gigabitAvail,
                availableSpeed,
                isPrivate,
                isHouseConnected,
                companyId,
                status: EVerificationStatus.verified,
                isHouseCottage,
            },
        },
    });
};

export const setAddressVerificationStatus = (currentVerifiedAddressData: IVerifiedAddressData, newStatus: EVerificationStatus): TSetAddressVerifiationAction => {
    return ({
        type: EContactDetailTypes.SET_VERIFIED_ADDRESS,
        payload: {
            verifiedAddressData: {
                ...currentVerifiedAddressData,
                status: newStatus,
            },
        },
    });
};

interface NotNullAddress extends IAddress {
    city: ICitiesOption,
    street: ISelectInputOption,
    house: ISelectInputOption,
    flat: string,
}

const getNotNullAddressData = (editedAddressData: IAddress | null): NotNullAddress | null => {
    if (!editedAddressData) {
        return null;
    }
    const { city, street, house, flat } = editedAddressData;

    if (!city || !street || !house) {
        return null;
    }

    return {
        city,
        street,
        house,
        flat: flat || '',
    };
};

/**
 * Проверка доступности услуг на адресе
 *
 * @param checkBySelectedTariff флаг указывающий на необоходимость "глубокой" проверки пришедших данных по адресу
 * @param isMono флаг выбанной моно услуги, в противном случае кп
 *
 * По умолчанию оба флага задизейблены для обратной совместимости
 */
export const checkAddress = (checkBySelectedTariff = false, isMono = false, gtmSenderCallback?: (action: string) => void) => {
    return async(dispatch: TDispatch, getState: () => IContactAddressState): Promise<void> => {
        const state = getState();
        let availableSpeed = EAvailableSpeed.between100and1000;

        const { editedAddressData, verifiedAddressData } = state.contactAddress;

        const addressData = getNotNullAddressData(editedAddressData);

        if (!addressData) {
            return;
        }

        if (isEqual(addressData, verifiedAddressData.address)) {
            return;
        }

        const { city, street, house, flat } = addressData;

        const houseLabel = house?.label;
        const houseLabelHasSlash = houseLabel.includes('/');
        const houseNum = houseLabelHasSlash ? houseLabel.substring(0, houseLabel.indexOf('/')) : houseLabel;
        const buildNum = houseLabelHasSlash ? houseLabel.substring(houseLabel.indexOf('/') + 1) : '';

        const data = {
            street: street?.value,
            houseNum: houseNum,
            office: flat,
            houseBuild: buildNum,
            saleAgentClientName: ESaleAgentClientName.FULL_BUY_SITE,
            address: selectAddressByString(state),
        };

        const { providerId } = city.value;

        dispatch(setAddressVerificationStatus(verifiedAddressData, EVerificationStatus.verifying));

        const resp = await doRequest.fullBuy<ResponseDto>(
            addressInfo(providerId, data),
        );

        let analyticsMessage = 'error';

        if (resp.isSuccess) {
            const [firstElement] = resp.payload.result;
            const isPrivate = resp.payload.houseFeatures.isPrivate || 0;
            let isHouseConnected: boolean;

            if (firstElement && checkBySelectedTariff) {
                isHouseConnected = isMono ? Boolean(firstElement.int) : Boolean(firstElement.domrutv && firstElement.int);
            } else {
                isHouseConnected = !!firstElement;
            }

            const { gigabitAvail, gigabitPlusAvailable } = resp.payload;

            if (gigabitAvail === 0) {
                availableSpeed = EAvailableSpeed.lessOrEqual100;
            }

            if (gigabitPlusAvailable === 1 || gigabitPlusAvailable === 2) {
                availableSpeed = EAvailableSpeed.more1000;
            }

            const companyId = firstElement?.company_id || 0;

            dispatch(setAddressVerification({
                address: addressData,
                gigabitAvail, // remove
                isHouseConnected,
                companyId,
                isPrivate, // remove

                isHouseCottage: Boolean(isPrivate),
                availableSpeed,
            }));

            if (isHouseConnected) {
                analyticsMessage = gigabitAvail ? 'success' : 'success_100mb';
            }
        } else {
            dispatch(setAddressVerificationStatus(verifiedAddressData, EVerificationStatus.error));
        }

        typeof gtmSenderCallback === 'function' && gtmSenderCallback(analyticsMessage);
    };
};
